/**
 * Canonical serialization and content hashing for an encounter, the multi-
 * aircraft counterpart of `flight/snapshot.ts`. Same contract, same reason:
 * the regression suite records a trace once and asserts the hash forever, and
 * a lockstep desync check needs one number to compare.
 *
 * Every per-aircraft sim goes through `toSnapshot`, so the flight core's own
 * canonical form is reused rather than reinvented; the combat layer adds its
 * own fields around it. The rounds in flight are hashed too — a replay that
 * drew different rounds is a replay that fired different rounds.
 */

import { canonicalJson, contentHash } from "../flight/hash.ts";
import { toSnapshot } from "../flight/snapshot.ts";
import type { AiPilotState } from "./ai-pilot.ts";
import type { CombatAircraft, EncounterState } from "./encounter.ts";
import type { RouteOrders, RouteState } from "./route.ts";
import type { EncounterTarget } from "./targets.ts";
import type { BombRecord } from "./bombs.ts";
import type { FlakBattery } from "./flak.ts";
import type { RoundRecord } from "./rounds.ts";
import type { SceneryFire } from "./scenery-fires.ts";
import type { CrashSite } from "./crash-sites.ts";
import type { ParachuteState } from "./parachute.ts";

function aiSnapshot(ai: AiPilotState | null): Record<string, unknown> | null {
  if (!ai) return null;
  return {
    skill: ai.skill,
    mode: ai.mode,
    maneuver: ai.command.id,
    direction: ai.command.direction,
    maneuverSeconds: ai.maneuverSeconds,
    triggerSeconds: ai.triggerSeconds,
    firing: ai.firing,
    aimErrorAzRad: ai.aimErrorAzRad,
    aimErrorElRad: ai.aimErrorElRad,
  };
}

function roundSnapshot(round: RoundRecord): Record<string, unknown> {
  return {
    shooterId: round.shooterId,
    roundIndex: round.roundIndex,
    from: { ...round.from },
    direction: { ...round.direction },
    spawnTick: round.spawnTick,
    damageRaw: round.damageRaw,
    tracer: round.tracer,
  };
}

/** Only the cursor, not the waypoint list — a route's legs never change mid-flight. */
function routeSnapshot(route: RouteState | null, orders: RouteOrders): Record<string, unknown> | null {
  if (!route) return null;
  return { index: route.index, complete: route.complete, legs: route.waypoints.length, orders };
}

function targetSnapshot(target: EncounterTarget): Record<string, unknown> {
  return {
    id: target.id,
    kind: target.kind,
    side: target.side,
    position: { ...target.position },
    integrity: target.integrity,
    burning: target.burning,
    burnSeconds: target.burnSeconds,
    destroyed: target.destroyed,
    hitsTaken: target.hitsTaken,
    bombsTaken: target.bombsTaken,
    bombsRemaining: target.bombsRemaining,
    bombsReleased: target.bombsReleased,
    lastReleaseTick: target.lastReleaseTick,
    lastAttackerId: target.lastAttackerId,
    // An airship's belts are state and therefore fingerprint: a replay in which
    // the Zeppelin's crown gunner ran dry is not the same fight as one in which
    // he did not. Empty on every unarmed kind, so this costs a `[]` and nothing
    // else on a balloon or a shed.
    gunRoundsRemaining: [...target.gunRoundsRemaining],
    gunRoundsFired: target.gunRoundsFired,
    gunAimBias: [...target.gunAimBias],
    gunAimEpoch: target.gunAimEpoch,
  };
}

function bombSnapshot(bomb: BombRecord): Record<string, unknown> {
  return {
    bomberId: bomb.bomberId,
    bombIndex: bomb.bombIndex,
    from: { ...bomb.from },
    velocity: { ...bomb.velocity },
    releaseTick: bomb.releaseTick,
    impactY: bomb.impactY,
  };
}

function aircraftSnapshot(entry: CombatAircraft): Record<string, unknown> {
  return {
    id: entry.id,
    side: entry.side,
    status: entry.status,
    fallingSeconds: entry.fallingSeconds,
    fire: { ...entry.fire },
    roundsFired: entry.roundsFired,
    hitsTaken: entry.hitsTaken,
    hitsScored: entry.hitsScored,
    underFire: entry.underFire,
    lastAttackerId: entry.lastAttackerId,
    ammunition: entry.ammunition,
    bombsRemaining: entry.bombsRemaining,
    bombsReleased: entry.bombsReleased,
    lastReleaseTick: entry.lastReleaseTick,
    quarryId: entry.quarryId,
    route: routeSnapshot(entry.route, entry.routeOrders),
    ai: aiSnapshot(entry.ai),
    sim: toSnapshot(entry.sim),
  };
}

/**
 * A battery's laying, not its emplacement: position, side and class come from
 * the setup and cannot change, so hashing them would only make the number
 * longer. The bursts are not hashed *because they are not state* — `flak.ts`
 * resolves a shell where it is created, so a burst is this tick's output and
 * the hits it caused are already visible in the aircraft it damaged.
 */
function flakSnapshot(battery: FlakBattery): Record<string, unknown> {
  return {
    id: battery.id,
    slots: battery.slots.map((slot) => ({
      targetId: slot.targetId,
      timerS: slot.timerS,
      trackingShots: slot.trackingShots,
    })),
  };
}

/**
 * The burning-scenery list, or `undefined` when nothing is alight (AD-018).
 *
 * `undefined` and not `[]` on purpose, and it is the whole compatibility story
 * for this field: `canonicalJson` drops undefined keys, so an encounter with no
 * scenery fires serializes to **exactly the string it did before this field
 * existed**. Every recorded regression hash, every replay tape and
 * `engine-fingerprint.ts`'s own probe — which injects no `SceneryQuery` and can
 * therefore never light anything — are unmoved, and no `SNAPSHOT_VERSION` bump
 * is owed (that number guards `flight/snapshot.ts`'s `SimState`, which this
 * lane did not touch at all).
 *
 * `sinceHitSeconds` is hashed alongside the rest because it is real state: it
 * decides whether a smouldering barn is still primed, so two runs that
 * disagreed about it would diverge on the next round that went in.
 */
function sceneryFireSnapshot(fires: readonly SceneryFire[]): Record<string, unknown>[] | undefined {
  if (fires.length === 0) return undefined;
  return fires.map((fire) => ({
    key: fire.key,
    x: fire.x,
    y: fire.y,
    z: fire.z,
    spreadM: fire.spreadM,
    hits: fire.hits,
    burnSeconds: fire.burnSeconds,
    sinceHitSeconds: fire.sinceHitSeconds,
  }));
}

/**
 * The crash-site list, or `undefined` when nobody has gone in yet (AD-014c).
 *
 * `undefined` rather than `[]` for exactly the reason `sceneryFireSnapshot`
 * gives: `canonicalJson` drops undefined keys, so an encounter in which nothing
 * has reached the ground serializes to the string it did before this field
 * existed. `engine-fingerprint.ts`'s probe flies 240 ticks and lands nothing, so
 * the fingerprint is unmoved by the field's mere presence, and no
 * `SNAPSHOT_VERSION` bump is owed — that number guards `flight/snapshot.ts`'s
 * `SimState`, which this has not touched.
 *
 * `ageSeconds` is hashed because it is real state: it is what the flame and the
 * column are drawn from, so two peers that disagreed about it would show
 * different fires over the same field.
 */
function crashSiteSnapshot(sites: readonly CrashSite[]): Record<string, unknown>[] | undefined {
  if (sites.length === 0) return undefined;
  return sites.map((site) => ({
    id: site.id,
    aircraft: site.aircraft,
    x: site.x,
    y: site.y,
    z: site.z,
    headingRad: site.headingRad,
    ageSeconds: site.ageSeconds,
    burning: site.burning,
  }));
}

/**
 * The pilot under a canopy, or `undefined` when nobody has jumped (AD-021).
 *
 * `undefined` for exactly the reason `sceneryFireSnapshot` and
 * `crashSiteSnapshot` give above: `canonicalJson` drops undefined keys, so an
 * encounter nobody bailed out of — which is every encounter in every census,
 * every replay tape and `engine-fingerprint.ts`'s own probe — serializes to the
 * string it did before this field existed.
 *
 * `fallSeconds` is hashed because it is real state and not a readout: it is
 * what decides whether the canopy has taken his weight yet, so two peers that
 * disagreed about it would disagree about whether he lands or dies.
 * `weather`, `windEnabled` and `tunables` are **not** hashed — they are copied
 * from the jumping aeroplane's own `SimConfig`, which is already hashed under
 * that aeroplane, so hashing them here would only make the string longer.
 */
function parachuteSnapshot(state: ParachuteState | undefined): Record<string, unknown> | undefined {
  if (!state) return undefined;
  return {
    pilotId: state.pilotId,
    position: { ...state.position },
    velocity: { ...state.velocity },
    status: state.status,
    fallSeconds: state.fallSeconds,
  };
}

export function toEncounterSnapshot(state: EncounterState): Record<string, unknown> {
  return {
    seed: state.seed,
    tick: state.tick,
    timeS: state.timeS,
    status: state.status,
    aircraft: state.aircraft.map(aircraftSnapshot),
    targets: state.targets.map(targetSnapshot),
    flak: state.flak.map(flakSnapshot),
    rng: { ...state.rng },
    rounds: state.rounds.map(roundSnapshot),
    bombs: state.bombs.map(bombSnapshot),
    bombImpacts: state.bombImpacts.map((impact) => ({
      bomberId: impact.bomberId,
      bombIndex: impact.bombIndex,
      position: { ...impact.position },
    })),
    sceneryFires: sceneryFireSnapshot(state.sceneryFires),
    crashSites: crashSiteSnapshot(state.crashSites),
    parachute: parachuteSnapshot(state.parachute),
    events: state.events.map((event) => ({ ...event })),
  };
}

export function serializeEncounter(state: EncounterState): string {
  return canonicalJson(toEncounterSnapshot(state));
}

/** 64-bit fingerprint of a whole encounter. Equal hashes mean equal fights, tick for tick. */
export function encounterHash(state: EncounterState): string {
  return contentHash(serializeEncounter(state));
}
